import React, { useState, useRef, useCallback } from 'react';
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Image, Undo, Redo, Type } from 'lucide-react';

/**
 * Simple rich text editor using contentEditable.
 * Supports: paragraphs, bold, italic, headings, lists, inline images.
 */
export default function SimpleEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value);

  const exec = useCallback((command, val = null) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    if (onChange) onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.textContent || '';
    setIsEmpty(!text.trim());
    if (onChange) onChange(html);
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const insertImage = useCallback(() => {
    const url = window.prompt('Image URL:');
    if (url) {
      exec('insertHTML', `<img src="${url}" alt="" style="max-width:100%;border-radius:8px;margin:12px 0;" />`);
    }
  }, [exec]);

  const formatBlock = useCallback((tag) => {
    exec('formatBlock', tag);
  }, [exec]);

  const btnClass = "p-1.5 rounded hover:bg-stone-200 transition-colors text-stone-600 hover:text-stone-900";

  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden bg-white" data-testid="simple-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-stone-200 bg-stone-50 flex-wrap">
        <button type="button" onClick={() => formatBlock('p')} className={btnClass} title="Paragraph">
          <Type className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => formatBlock('h2')} className={btnClass} title="Heading 2">
          <Heading2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => formatBlock('h3')} className={btnClass} title="Heading 3">
          <Heading3 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-stone-300 mx-1" />
        <button type="button" onClick={() => exec('bold')} className={btnClass} title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => exec('italic')} className={btnClass} title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-stone-300 mx-1" />
        <button type="button" onClick={() => exec('insertUnorderedList')} className={btnClass} title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => exec('insertOrderedList')} className={btnClass} title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-stone-300 mx-1" />
        <button type="button" onClick={insertImage} className={btnClass} title="Insert Image">
          <Image className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-stone-300 mx-1" />
        <button type="button" onClick={() => exec('undo')} className={btnClass} title="Undo">
          <Undo className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => exec('redo')} className={btnClass} title="Redo">
          <Redo className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Area */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-3 left-4 text-stone-400 pointer-events-none text-sm">
            {placeholder || 'Start writing...'}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className="min-h-[300px] p-4 text-sm text-stone-800 focus:outline-none prose prose-sm max-w-none
            [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
            [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
            [&_li]:mb-1 [&_img]:rounded-lg [&_img]:max-w-full"
          onInput={handleInput}
          onPaste={handlePaste}
          dangerouslySetInnerHTML={{ __html: value || '' }}
          data-testid="editor-content"
        />
      </div>
    </div>
  );
}
